import { useSettings } from "~/store/settings";
import signalRConnection from "~/utils/signalr/signalRConnection";
import { useAuth } from "~/store/auth";
import api from "~/utils/auth/api";

declare global {
	interface Window {
		dataLayer: {
			push: ({ event, params }: { event: string; params: unknown }) => void;
		};
	}
}

export default defineNuxtPlugin((nuxtApp) => {
	const { signalrDomain } = nuxtApp.$config.public;
	const route = useRoute();
	const settings = useSettings();

	const { signalR, start } = signalRConnection({ url: signalrDomain + "/hubs/platformevent", id: "Analytics" });
	start();

	const signalREvent = (eventName: string, params?: Object) => {
		const auth = useAuth();
		const getCurrentDate = () => {
			const date = new Date();
			const year = date.toLocaleString("default", { year: "numeric" });
			const month = date.toLocaleString("default", { month: "2-digit" });
			const day = date.toLocaleString("default", { day: "2-digit" });

			return `${year}-${month}-${day}`;
		};

		const webPlatform = () => {
			if (window.innerWidth <= 1280) {
				return "Web Mobile";
			} else return "Web Desktop";
		};

		const platformName = computed(() => (settings.appType !== "undefined" && settings.appType !== "" ? settings.appType : webPlatform()));

		try {
			const ST = useCookie("ST");
			const MT = useCookie("MT");
			const BTAGCOOKIE = useCookie("BTAGCOOKIE");
			const REFERRERBTAGCOOKIE = useCookie("REFERRERBTAGCOOKIE");
			const data = {
				type: eventName,
				countryCode: settings.getCountryConfig?.countryIsoTwo,
				data: { ...params, accountId: auth.profile?.id, eventDate: getCurrentDate() },
				sessionInformation: {
					platformName: platformName.value,
					dataFree: false,
					sessionTrackingToken: ST.value || "",
					isSynapse: true,
					bannerTag: BTAGCOOKIE.value || "",
					queryParams: JSON.stringify(route.params) || "",
					latestRefererBannerTag: REFERRERBTAGCOOKIE.value || "",
					masterToken: MT.value || "",
					sessionToken: ST.value || "",
					appsFlyerExternalRef: settings.appsFlyerData,
				},
			};
			signalR.invoke("Store", data);
		} catch (e) {
			console.log(e);
		}
	};

	const gtmEvent = (eventName: string, params?: unknown) => {
		try {
			if (!settings.isDataFree) {
				window.dataLayer.push({ event: eventName, params });
			}
		} catch (e) {}
	};

	const feedSessionUsageTracking = () => {
		try {
			if (!sessionStorage.getItem("FeedTracked")) {
				const auth = useAuth();
				const accountId = auth.profile?.id;
				if (accountId) {
					api({
						url: "/FeedAndFollowing/UpdateFeedUsedSession",
						type: "api",
						options: {
							method: "POST",
							headers: { "X-AccountId": accountId },
						},
						data: null,
					});
				}
			}
			sessionStorage.setItem("FeedTracked", "true");
		} catch (e) {
			console.log(e);
		}
	};

	return {
		provide: {
			trackEvent: {
				launchGame: (game: string, msisdn: string, vertical: string, fromPath: string, currentPath: string) => {
					gtmEvent("launch_real_game", {
						game,
						msisdn,
						vertical,
						fromPath,
						currentPath,
					});
				},
				likeGame: (game: string, likes: number) => {
					gtmEvent("like_game", {
						game,
						likes,
					});
				},
				dislikeGame: (game: string, likes: number) => {
					gtmEvent("dislike_game", {
						game,
						likes,
					});
				},
				favouriteGameAdd: (game: string, msisdn: string) => {
					gtmEvent("favourite_game_add", {
						game,
						msisdn,
					});
				},
				favouriteGameRemove: (game: string, msisdn: string) => {
					gtmEvent("favourite_game_remove", {
						game,
						msisdn,
					});
				},
				socialShare: (game: string, msisdn: string, channel: string) => {
					gtmEvent("game_social_share", {
						game,
						msisdn,
						channel,
					});
				},
				categoryNav: (name: string) => {
					gtmEvent("lobbies_category_nav", {
						name,
					});
				},
				betConfirmationEvent: (value: string) => {
					gtmEvent("bet_confirmation_event", value);
				},
				loadPage: (params: { title?: string; url?: string; path?: string; search?: string; width?: string; height?: string }) => {
					gtmEvent("page_view", params);
				},
				addBet: (bet: unknown) => {
					gtmEvent("AddBet", { bet });
				},
				deepLinkEdit: () => {
					gtmEvent("deepLinkEdit");
				},
				generateFixturesPdf: () => {
					gtmEvent("GenerateFixturesPdf", {});
				},
				strikeBets: (bets: Array<unknown>) => {
					gtmEvent("PlaceBetEvent", { bets });
				},
				cashout: (betslipId: number) => {
					gtmEvent("CashOut", { betslipId });
				},
				partialCashout: (percentage: number) => {
					gtmEvent("PartialCashout", { percentage });
				},
				login: (msisdn: string) => {
					gtmEvent("LoginInitiatedEvent", {
						msisdn,
					});
				},
				loginComplete: (msisdn: string) => {
					gtmEvent("LoginSuccessEvent", {
						msisdn,
					});
					signalREvent("login", { msisdn });
				},
				loginFailure: (username: string, reason: string) => {
					gtmEvent("LoginFailureEvent", {
						username,
						reason,
					});
				},
				register: () => {
					gtmEvent("RegistrationInitatedEvent");
				},
				registerComplete: (userID: string, bannerTag: string, sessionToken: string, masterToken: string) => {
					gtmEvent("RegistrationCompletedEvent", {
						player_id: userID,
						banner_tag: bannerTag,
						session_token: sessionToken,
						master_token: masterToken,
					});
					signalREvent("registrationCompleted", {
						player_id: userID,
						banner_tag: bannerTag,
						session_token: sessionToken,
						master_token: masterToken,
					});
				},
				placeJackpotBet: (name: string) => {
					gtmEvent("PlaceJackpotBet", { name });
				},
				oddsOnClicked: () => {
					gtmEvent("OddsOnClicked");
				},
				oddsOnSelected: (numberOfSelections: number) => {
					gtmEvent("OddsOnSelected", { numberOfSelections });
				},
				topLeaguesClosed: () => {
					gtmEvent("topLeaguesClosed");
				},
				quickBetKeyboardClicked: (buttonClicked: string) => {
					gtmEvent("QuickBetKeyboardClicked", { buttonClicked });
				},
				footerClicked: (title: string, menutItem: string) => {
					gtmEvent("FooterClickEvent", { title, menutItem });
				},
				topLeagueSelected: (name: string) => {
					gtmEvent("topLeagueSelected", { name });
				},
				betwayBulbClicked: (sport: string, league: string) => {
					gtmEvent("betwayBulbClicked", { sport, league });
				},
				toggleFixtureType: (fixtureType: string) => {
					gtmEvent("ToggleFixtureType", { fixtureType });
				},
				filter: (filter: unknown, sportId: string) => {
					gtmEvent("FilterSport", { filter, sportId });
				},
				moreMarkets: (eventId: string) => {
					gtmEvent("NavigatetoMoreMarkers", { eventId });
				},
				toggleWidget: (marketId: string) => {
					gtmEvent("ToggleWidget", { marketId });
				},
				switchSport: (sportId: string) => {
					gtmEvent("SwitchSports", { sportId });
				},
				toggleSearch: () => {
					gtmEvent("ToggleSearch");
				},
				searchQuery: (searchQuery: string) => {
					gtmEvent("Search", { searchQuery });
				},
				toggleHamburgerMenu: () => {
					gtmEvent("OpenHamburgerMenu");
				},
				headerNav: (name: string) => {
					gtmEvent("HeaderNav", { name });
				},
				topEventNav: (name: string) => {
					gtmEvent("topEventNav", { name });
				},
				toggleLanguage: (locale: string) => {
					gtmEvent("SwitchLanguage", { locale });
				},
				togglePriceType: (priceType: string) => {
					gtmEvent("PriceType", { priceType });
				},
				bigWinnerWidgetPanelInteraction: (interactionType: string, betslipId: string) => {
					gtmEvent("BigWinnersWidgetInteractionEvent", {
						interactionType,
						betslipId,
					});
				},
				topSixNav: (path: string) => {
					gtmEvent("TopSixNavigationInteraction", { path });
				},
				welcomePage: (id: string, path: string) => {
					gtmEvent("WelcomePageInteraction", { id, path });
				},
				gameLaunch: (gameLaunchInfo: Object) => {
					gtmEvent("GameLaunchEvent", gameLaunchInfo);
				},
				bannerClick: () => {
					gtmEvent("BannerCtaClickEvent");
				},
				bannerInfoClick: () => {
					gtmEvent("MoreInfoCtaClickEvent");
				},
				FeedOnboardingVisible: () => {
					gtmEvent("FeedOnboardingVisible");
				},
				FeedOnboardingSportClicked: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedOnboardingSportClicked");
				},
				FeedOnboardingCountryClicked: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedOnboardingCountryClicked");
				},
				FeedOnboardingFollowClicked: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedOnboardingFollowClicked");
				},
				FeedOpenEventPageClicked: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedOpenEventPageClicked");
				},
				FeedAddOrRemoveBetslipOutcome: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedAddOrRemoveBetslipOutcome");
				},
				FeedFollowEntity: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedFollowEntity");
				},
				FeedOnboardingCompleted: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedOnboardingCompleted");
				},
				FeedSearchPanelClicked: () => {
					gtmEvent("FeedSearchPanelClicked");
				},
				FeedSharePost: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedSharePost");
				},
				FeedLaunched: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedLaunched");
				},
				FeedsCommentLaunch: () => {
					feedSessionUsageTracking();
					gtmEvent("FeedsCommentLaunch");
				},
				FeedsCommentPost: (id: String) => {
					feedSessionUsageTracking();
					gtmEvent("FeedsCommentPost", { id });
				},
				homePageVertical: (vertical: string) => {
					gtmEvent("HomePageVertical", { vertical });
				},
			},
		},
	};
});
